import type {
	PriceImportApply,
	PriceImportApplyRow,
	PriceImportCardRow,
	PriceImportPriceToday
} from "~/services/aiOrchestrator";

import i18n from "~/i18n/i18n";

const translatePriceImport = (key: string, options?: Record<string, string>) =>
	i18n.t(`fernieConfirm.priceImport.${key}`, { ns: "common", ...options });

type PricePart = Pick<
	PriceImportCardRow | PriceImportPriceToday,
	"knownCostPerEach" | "royaltyIncluded" | "royaltyPerEach" | "royaltyState"
>;

export const formatRoyalty = ({ royaltyPerEach, royaltyState }: PricePart): string => {
	switch (royaltyState) {
		case "SPECIFIED":
			return royaltyPerEach ?? translatePriceImport("unknownAmount");
		case "CONFIRMED_NONE":
			return "0";
		case "LEGACY_UNSPECIFIED":
			return translatePriceImport("noSeparateRoyalty");
		default:
			return translatePriceImport("royaltyUnconfirmed");
	}
};

export const formatCost = ({ knownCostPerEach, royaltyIncluded }: PricePart): string => {
	if (!knownCostPerEach) return translatePriceImport("unknownAmount");
	return royaltyIncluded ? knownCostPerEach : translatePriceImport("baseOnly", { amount: knownCostPerEach });
};

export const formatAppliedSubtotal = ({
	knownSubtotalPerEach,
	royaltyStatus
}: Pick<PriceImportApplyRow, "knownSubtotalPerEach" | "royaltyStatus">): string | undefined => {
	if (!knownSubtotalPerEach) return undefined;
	switch (royaltyStatus) {
		case "REVIEWED_UNCONFIRMED":
			return translatePriceImport("subtotalUnconfirmed", { amount: knownSubtotalPerEach });
		case "LEGACY_UNSPECIFIED":
			return translatePriceImport("subtotalNoSeparateRoyalty", { amount: knownSubtotalPerEach });
		default:
			return translatePriceImport("knownCost", { amount: knownSubtotalPerEach });
	}
};

export const isApplySuccess = (apply: PriceImportApply | undefined): boolean => apply?.outcome === "COMPLETE";

export const formatLabeledValue = (key: string, value: string) => translatePriceImport(key, { value });

export const formatPriceChange = (oldValue: string | undefined, newValue: string) =>
	oldValue === undefined ? newValue : translatePriceImport("change", { new: newValue, old: oldValue });

export default translatePriceImport;
